import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { ListHeader, LoadingSpinner } from "@msys/ui";
import { List, ListItem, ListItemText, Stack, Switch } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import {
  SupplierCataloguesBox__SupplierCatalogueFragment,
  useSupplierCataloguesBoxQuery,
  useSupplierCataloguesBox__SubscribeToMutation,
  useSupplierCataloguesBox__UnsubscribeFromMutation,
} from "./SupplierCatalogues.generated.js";

interface Props {}

export const SupplierCataloguesBox = ({ ...props }: Props) => {
  const { supplierId } = useParams();
  if (!supplierId) throw new Error("Supplier id is missing");

  const client = useApolloClient();
  // const { getFormattedDate } = useFormatting();

  const { t } = useTranslate(["SupplierCatalogue"]);

  const crmCompanySupplierCataloguesBoxQuery = useSupplierCataloguesBoxQuery({
    client,
    variables: { supplierId: supplierId },
  });

  const [
    subscribeToSupplierCatalogue,
    { loading: subscribeToSupplierCatalogueLoading },
  ] = useSupplierCataloguesBox__SubscribeToMutation({
    client,
    refetchQueries: [namedOperations.Query.SupplierCataloguesBox],
  });

  const [
    unsubscribeFromSupplierCatalogue,
    { loading: unsubscribeFromSupplierCatalogueLoading },
  ] = useSupplierCataloguesBox__UnsubscribeFromMutation({
    client,
    refetchQueries: [namedOperations.Query.SupplierCataloguesBox],
  });

  const supplierCatalogues = getDataOrNull(
    crmCompanySupplierCataloguesBoxQuery.data?.pimGetSupplierCatalogues
  )?.supplierCatalogues;

  const changeSupplierCatalogueSubscription = useCallback(
    async (
      supplierCatalogue: SupplierCataloguesBox__SupplierCatalogueFragment
    ) => {
      if (supplierCatalogue.supplierCatalogueSubscription) {
        await unsubscribeFromSupplierCatalogue({
          variables: {
            input: {
              supplierId: supplierId,
              supplierCatalogueId: supplierCatalogue.id,
            },
          },
        });
      } else {
        await subscribeToSupplierCatalogue({
          variables: {
            input: {
              supplierId: supplierId,
              supplierCatalogueId: supplierCatalogue.id,
            },
          },
        });
      }
    },
    [subscribeToSupplierCatalogue, unsubscribeFromSupplierCatalogue, supplierId]
  );

  return supplierCatalogues && supplierCatalogues.length > 0 ? (
    <>
      <ListHeader
        ActionButtons={
          unsubscribeFromSupplierCatalogueLoading ||
          subscribeToSupplierCatalogueLoading ? (
            <LoadingSpinner size="s" />
          ) : undefined
        }
        sx={{ marginBottom: 0.5 }}
      />
      <List disablePadding>
        {supplierCatalogues.map((supplierCatalogue, index, array) => (
          <ListItem
            key={supplierCatalogue.id}
            divider={index < array.length - 1}
          >
            <ListItemText
              primary={
                supplierCatalogue.title
                  ? supplierCatalogue.title
                  : supplierCatalogue.isDefault
                    ? t("Default catalogue", { ns: "SupplierCatalogue" })
                    : "-"
              }
            />

            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "flex-end",
              }}
            >
              <Switch
                checked={!!supplierCatalogue.supplierCatalogueSubscription}
                onClick={async () => {
                  await changeSupplierCatalogueSubscription(supplierCatalogue);
                }}
                color="primary"
              />
            </Stack>
          </ListItem>
        ))}
      </List>
    </>
  ) : null;
};
